import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiUpload, FiBriefcase, FiClipboard, FiBarChart, FiEdit } from 'react-icons/fi';
import { useOnboarding } from '../components/onboarding/OnboardingContext';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { completeOnboarding } = useOnboarding();
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      title: "Welcome to ResuMemo",
      description: "Your all-in-one job application assistant. Let's get you started with a quick tour of how ResuMemo can help you land your dream job.",
      image: "/assets/Pic1.png",
      icon: <FiClipboard className="h-12 w-12 text-primary-500" />
    },
    {
      title: "Upload Your Resume",
      description: "Start by uploading your resume. ResuMemo supports PDF and DOCX formats, or you can paste your resume text directly. We'll analyze your skills and experience automatically.",
      image: "/assets/Parsing.png",
      icon: <FiUpload className="h-12 w-12 text-primary-500" />
    },
    {
      title: "Discover Your Strengths",
      description: "ResuMemo analyzes your resume to identify your key strengths, skills, and experiences. Get insights about what makes you stand out to employers.",
      image: "/assets/Strength.png",
      icon: <FiBarChart className="h-12 w-12 text-primary-500" />
    },
    {
      title: "Save Job Descriptions",
      description: "Use our browser extension to save job descriptions with one click while browsing job boards. ResuMemo will automatically parse and store the job details.",
      image: "/assets/extension.png",
      icon: <FiBriefcase className="h-12 w-12 text-primary-500" />
    },
    {
      title: "Match Analysis",
      description: "See how well your resume matches each job. ResuMemo provides a detailed analysis of your fit and suggests improvements to increase your chances.",
      image: "/assets/JobMatch.png",
      icon: <FiBarChart className="h-12 w-12 text-primary-500" />
    },
    {
      title: "Tailor Your Resume",
      description: "Create job-specific versions of your resume with AI assistance. ResuMemo helps you highlight relevant skills and experiences for each application.",
      image: "/assets/JobFit.png",
      icon: <FiEdit className="h-12 w-12 text-primary-500" />
    }
  ];

  const handleComplete = useCallback(() => {
    completeOnboarding();
    navigate('/dashboard');
  }, [completeOnboarding, navigate]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  }, [currentStep, steps.length, handleComplete]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    completeOnboarding();
    navigate('/dashboard');
  }, [completeOnboarding, navigate]);

  // Arrow-key navigation for the entire page
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') handleNext();
      else if (e.key === 'ArrowLeft') handlePrevious();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrevious]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Progress bar */}
      <div
        className="w-full bg-gray-200 h-1 fixed top-0 z-10"
        role="progressbar"
        aria-valuenow={currentStep + 1}
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-label={`Step ${currentStep + 1} of ${steps.length}`}
      >
        <div
          className="bg-primary-600 h-1 transition-all duration-300 ease-in-out"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        ></div>
      </div>
      
      <div className="flex-grow flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden max-w-6xl w-full">
          <div className="flex flex-col md:flex-row">
            {/* Left side - Image */}
            <div className="w-full md:w-1/2 bg-primary-50 p-8 flex items-center justify-center">
              <div className="relative w-full max-w-md">
                <div className="absolute inset-0 flex items-center justify-center opacity-20">
                  {steps[currentStep].icon}
                </div>
                <img 
                  src={steps[currentStep].image} 
                  alt={steps[currentStep].title}
                  className="object-contain w-full h-auto max-h-96 relative z-10"
                />
              </div>
            </div>
            
            {/* Right side - Content */}
            <div className="w-full md:w-1/2 p-8 flex flex-col">
              <div className="flex-grow">
                <div className="flex items-center mb-6">
                  <div className="p-3 bg-primary-100 rounded-full mr-4">
                    {steps[currentStep].icon}
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800">{steps[currentStep].title}</h2>
                </div>
                
                <p className="text-lg text-gray-600 mb-8">
                  {steps[currentStep].description}
                </p>
                
                {/* Step indicators */}
                <div className="flex justify-center mb-8" role="tablist" aria-label="Onboarding steps">
                  {steps.map((step, index) => (
                    <button
                      key={index}
                      role="tab"
                      onClick={() => setCurrentStep(index)}
                      className={`h-3 w-3 mx-1 rounded-full transition-all duration-200 ${
                        currentStep === index ? 'bg-primary-600 w-6' : 'bg-gray-300'
                      }`}
                      aria-label={`Go to step ${index + 1}: ${step.title}`}
                      aria-selected={currentStep === index}
                      tabIndex={currentStep === index ? 0 : -1}
                    ></button>
                  ))}
                </div>
              </div>
              
              {/* Navigation buttons */}
              <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                <div>
                  {currentStep > 0 && (
                    <button
                      onClick={handlePrevious}
                      className="px-6 py-2 text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Back
                    </button>
                  )}
                </div>
                
                <div className="flex space-x-4">
                  <button
                    onClick={handleSkip}
                    className="px-6 py-2 text-gray-500 hover:text-gray-700 font-medium"
                  >
                    Skip Tour
                  </button>
                  
                  <button
                    onClick={handleNext}
                    className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium flex items-center"
                  >
                    {currentStep < steps.length - 1 ? 'Next' : 'Get Started'} 
                    <FiArrowRight className="ml-2" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;